import { useEffect, type RefObject } from 'react'

export interface KeyboardActions {
  togglePlay: () => void
  seekBy: (delta: number) => void
  volumeBy: (delta: number) => void
  toggleMute: () => void
  toggleFullscreen: () => void
  toggleCaptions: () => void
  toggleTheater: () => void
  toggleHotkeys: () => void
  enabled: boolean
}

export function useKeyboardShortcuts(
  containerRef: RefObject<HTMLElement | null>,
  actions: KeyboardActions,
) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!actions.enabled) return
      const target = e.target as HTMLElement | null
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return
      }
      const root = containerRef.current
      if (!root) return
      if (
        document.activeElement &&
        !root.contains(document.activeElement) &&
        document.activeElement !== document.body
      ) {
        return
      }

      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          e.preventDefault()
          actions.togglePlay()
          break
        case 'arrowleft':
          e.preventDefault()
          actions.seekBy(-5)
          break
        case 'arrowright':
          e.preventDefault()
          actions.seekBy(5)
          break
        case 'j':
          e.preventDefault()
          actions.seekBy(-10)
          break
        case 'l':
          e.preventDefault()
          actions.seekBy(10)
          break
        case 'arrowup':
          e.preventDefault()
          actions.volumeBy(0.05)
          break
        case 'arrowdown':
          e.preventDefault()
          actions.volumeBy(-0.05)
          break
        case 'm':
          e.preventDefault()
          actions.toggleMute()
          break
        case 'f':
          e.preventDefault()
          actions.toggleFullscreen()
          break
        case 't':
          e.preventDefault()
          actions.toggleTheater()
          break
        case 'c':
          e.preventDefault()
          actions.toggleCaptions()
          break
        case '?':
          e.preventDefault()
          actions.toggleHotkeys()
          break
        default:
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [actions, containerRef])
}
